// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rediger › Geometri's Dæk / gulv tool (G; frontend-u0o.7, slice S7):
 * floor slabs, ceilings and roofs as the editable slab rows of ReUseX
 * schema v44 (`slab.*` ops, ruxd #34) — drawn as a polygon, taken from a
 * room's outline, cut with holes (stair openings, shafts), their kind,
 * level and thickness. A derived slab edited here becomes manual and
 * locked (the server's rule: the next solids run keeps it). A slab served
 * from the fallback element index (no rows yet: solids from before v44) is
 * not editable until the solids are re-derived. World coordinates. Pure:
 * no React, no DOM.
 */

import type { ModelEditOp, PlanXY, SolidSlab } from '../api/types';
import { findElement, slabLabel, spaceLabel, type GeometriModel } from './geometri';
import type { XY } from './planWalls';
import { ccw, cleanRing } from './roomTool';
import { formatAreaDa, pointInRing, ringArea } from './spaces';
import { ringSelfIntersects } from './geometri';

export type SlabMode = 'polygon' | 'room' | 'hole';
export type SlabKind = SolidSlab['kind'];

/** The Dæk tool's options strip. */
export interface SlabOptions {
  /** Tegn polygon | Fra rum (click a room) | Hul (draw a hole in the selected slab). */
  mode: SlabMode;
  kind: SlabKind;
  /** Metres. */
  thickness: number;
  /**
   * The top above the storey floor, metres; null = where the kind sits: a
   * floor's top on the storey floor, a ceiling's or roof's underside on its
   * ceiling.
   */
  level: number | null;
}

export const DEFAULT_SLAB_OPTIONS: SlabOptions = { mode: 'polygon', kind: 'floor', thickness: 0.25, level: null };

/** The smallest slab or hole, m². */
export const MIN_SLAB_AREA = 0.05;

const r6 = (v: number) => Math.round(v * 1e6) / 1e6;

const KIND_NEW: Record<SlabKind, string> = { floor: 'Nyt dæk', ceiling: 'Nyt loft', roof: 'Nyt tag' };
const KIND_NAME: Record<SlabKind, string> = { floor: 'Dæk', ceiling: 'Loft', roof: 'Tag' };

/** A slab row (schema v44), which the `slab.*` ops edit; the fallback element index has no `source`. */
export function isSlabRow(s: Pick<SolidSlab, 'source'>): boolean {
  return s.source !== undefined;
}

export const NOT_A_ROW = 'Dækket kan ikke redigeres, før dæk og tag er genberegnet';

/** Where a new slab's faces go on storey `storeyId` (world z). */
export function slabLevels(model: GeometriModel, storeyId: number, opts: Pick<SlabOptions, 'kind' | 'thickness' | 'level'>): { z_top: number; z_base: number } {
  const s = model.storeys?.find((x) => x.id === storeyId);
  const floor = s?.z_min ?? 0;
  const ceiling = s && s.z_max > s.z_min + 0.5 ? s.z_max : floor + 2.5;
  const t = opts.thickness > 0 ? opts.thickness : DEFAULT_SLAB_OPTIONS.thickness;
  const top = opts.level !== null ? floor + opts.level : opts.kind === 'floor' ? floor : ceiling + t;
  return { z_top: r6(top), z_base: r6(top - t) };
}

/** Why a drawn ring cannot be a slab (or a hole: `what`), or null. */
function ringProblem(ring: readonly XY[], what: 'Et dæk' | 'Et hul'): string | null {
  if (ring.length < 3) return `${what} skal have mindst tre hjørner`;
  if (ringSelfIntersects(ring)) return 'Omridset krydser sig selv';
  if (Math.abs(ringArea(ring)) < MIN_SLAB_AREA) return `${what === 'Et dæk' ? 'Dækket' : 'Hullet'} er for lille (under ${formatAreaDa(MIN_SLAB_AREA)})`;
  return null;
}

export type SlabResult = { ops: ModelEditOp[]; label: string } | { error: string };

/** Tegn polygon: the `slab.create` of a drawn outline on storey `storeyId`, or why not. */
export function createSlabOps(model: GeometriModel, points: readonly XY[], storeyId: number, opts: SlabOptions, guid: string): SlabResult {
  const ring = cleanRing(points);
  const problem = ringProblem(ring, 'Et dæk');
  if (problem) return { error: problem };
  return {
    ops: [{ op: 'slab.create', record: { guid, storey_id: storeyId, kind: opts.kind, polygon: { outer: ccw(ring), holes: [] }, ...slabLevels(model, storeyId, opts) } }],
    label: KIND_NEW[opts.kind],
  };
}

/** Fra rum: a slab of a room's outline (its holes too), on the room's storey. */
export function slabFromRoomOps(model: GeometriModel, spaceGuid: string, opts: SlabOptions, guid: string): SlabResult {
  const f = findElement(model, spaceGuid);
  if (!f || f.kind !== 'space') return { error: 'Klik i et rum' };
  const sp = f.row;
  if (sp.storey_id === null) return { error: 'Rummet står ikke på en etage' };
  const outer = ccw(sp.polygon.outer as XY[]);
  if (outer.length < 3) return { error: 'Rummet har intet omrids' };
  const what = opts.kind === 'floor' ? 'Dæk under' : opts.kind === 'ceiling' ? 'Loft over' : 'Tag over';
  return {
    ops: [
      {
        op: 'slab.create',
        record: { guid, storey_id: sp.storey_id, kind: opts.kind, polygon: { outer, holes: sp.polygon.holes.map((h) => h.map((p) => [r6(p[0]), r6(p[1])] as PlanXY)) }, ...slabLevels(model, sp.storey_id, opts) },
      },
    ],
    label: `${what} ${spaceLabel(sp)}`,
  };
}

function slabRow(model: GeometriModel, guid: string): SolidSlab | { error: string } {
  const f = findElement(model, guid);
  if (!f || f.kind !== 'slab') return { error: 'Vælg et dæk' };
  if (!isSlabRow(f.row)) return { error: NOT_A_ROW };
  return f.row;
}

/** Hul: a hole (stair opening, shaft) drawn inside a slab row's outline, clear of its other holes. */
export function addHoleOps(model: GeometriModel, slabGuid: string, points: readonly XY[]): SlabResult {
  const sl = slabRow(model, slabGuid);
  if ('error' in sl) return sl;
  const ring = cleanRing(points);
  const problem = ringProblem(ring, 'Et hul');
  if (problem) return { error: problem };
  const outer = sl.polygon.outer;
  if (!ring.every((p) => pointInRing(p[0], p[1], outer))) return { error: 'Hullet skal ligge inden for dækket' };
  if (sl.polygon.holes.some((h) => ring.some((p) => pointInRing(p[0], p[1], h)) || h.some((p) => pointInRing(p[0], p[1], ring)))) return { error: 'Hullet rammer et andet hul' };
  const hole = ccw(ring);
  return {
    ops: [{ op: 'slab.update', guid: slabGuid, set: { polygon: { outer, holes: [...sl.polygon.holes, hole] } } }],
    label: `Hul i ${slabLabel(sl)}`,
  };
}

/** Take hole `index` out of a slab row. */
export function removeHoleOps(model: GeometriModel, slabGuid: string, index: number): SlabResult {
  const sl = slabRow(model, slabGuid);
  if ('error' in sl) return sl;
  if (index < 0 || index >= sl.polygon.holes.length) return { error: 'Hullet findes ikke' };
  return {
    ops: [{ op: 'slab.update', guid: slabGuid, set: { polygon: { outer: sl.polygon.outer, holes: sl.polygon.holes.filter((_, i) => i !== index) } } }],
    label: `Fjern hul · ${slabLabel(sl)}`,
  };
}

/** The slab rows on storey `storeyId` holding plan point `p` (not in a hole), smallest first. */
export function slabsAt(model: GeometriModel, p: XY, storeyId: number | null): SolidSlab[] {
  return (model.slabs ?? [])
    .filter((s) => (storeyId === null || s.storey_id === storeyId) && pointInRing(p[0], p[1], s.polygon.outer) && !s.polygon.holes.some((h) => pointInRing(p[0], p[1], h)))
    .sort((a, b) => a.area - b.area);
}

/** "Dæk", "Loft", "Tag": a slab kind's name. */
export function slabKindName(kind: SlabKind): string {
  return KIND_NAME[kind] ?? 'Dæk';
}
